import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { ArrowLeft, Building2, Check, KeyRound, Send, ShieldCheck, Sparkles, BellRing, User, Users } from 'lucide-react';
import type { SignupWorkspace } from '@mailpilot/shared';
import { api, storeToken, type ResOf, ApiError } from '../lib/api';
import { deviceKind, deviceName } from '../lib/desktop';
import { qk } from '../lib/keys';
import { useDebounced } from '../hooks/useBrowser';
import { useMe } from '../hooks/useMe';
import { useJoinOrganization } from '../hooks/useWorkspace';
import { SocialSignIn } from '../components/layout/SocialSignIn';
import { BrandMark } from '../components/ui/BrandMark';
import { Button, ChipInput, Field, Input, InlineError, Notice, Spinner, spring } from '../components/ui';

/** Matches the server's minimum (apps/server/src/routes/auth.ts). */
const MIN_PASSWORD = 10;

function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-screen min-h-[100dvh] items-stretch">
      <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden p-10 text-white lg:flex" style={{ background: 'linear-gradient(150deg, rgb(var(--primary)) 0%, rgb(var(--primary-hover)) 45%, rgb(var(--second)) 125%)' }}>
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 left-10 h-96 w-96 rounded-full bg-fuchsia-400/20 blur-3xl" />
        <div className="relative flex items-center gap-2.5">
          <BrandMark size={40} />
          <span className="text-xl font-semibold">MailPilot</span>
        </div>
        <div className="relative space-y-6">
          <h2 className="text-3xl font-semibold leading-tight">Understand every conversation. Reply with confidence.</h2>
          <ul className="space-y-4 text-[0.9375rem] text-white/90">
            <li className="flex gap-3"><Sparkles className="h-5 w-5 shrink-0" /> AI suggestions you review before anything is sent.</li>
            <li className="flex gap-3"><Users className="h-5 w-5 shrink-0" /> Share team inboxes with your organization, organised by division.</li>
            <li className="flex gap-3"><ShieldCheck className="h-5 w-5 shrink-0" /> Instructions hidden inside emails are always ignored.</li>
            <li className="flex gap-3"><BellRing className="h-5 w-5 shrink-0" /> Persistent reminders until you explicitly acknowledge.</li>
          </ul>
        </div>
        <p className="relative text-xs text-white/70">Your mailbox content is never used to train a general model.</p>
      </aside>
      <main className="flex flex-1 items-center justify-center p-5">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={spring} className="w-full max-w-md">
          <div className="mb-6 flex items-center gap-2.5 lg:hidden">
            <BrandMark size={40} />
            <span className="text-xl font-semibold text-ink">MailPilot</span>
          </div>
          <div className="rounded-3xl bg-surface p-6 shadow-card sm:p-8">
            <h1 className="text-xl font-semibold text-ink">{title}</h1>
            {subtitle && <p className="mt-1 text-[0.8125rem] text-muted">{subtitle}</p>}
            <div className="mt-6">{children}</div>
          </div>
          {footer && <div className="mt-5 text-center text-[0.8125rem] text-muted">{footer}</div>}
          <p className="mt-4 flex justify-center gap-4 text-xs text-subtle">
            <Link to="/about" className="hover:text-ink">About</Link>
            <Link to="/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/terms" className="hover:text-ink">Terms</Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
}

function useAuthSuccess() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return async (res: Pick<ResOf<'POST /api/auth/login'>, 'token'>) => {
    await storeToken(res.token);
    await qc.invalidateQueries({ queryKey: qk.me });
    await qc.fetchQuery({ queryKey: qk.me, queryFn: () => api('GET /api/auth/me') });
    const next = params.get('next');
    navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/inbox', { replace: true });
  };
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Two-step sign-in: shown after the server asks for a code.
  const [needCode, setNeedCode] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const done = useAuthSuccess();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api('POST /api/auth/login', { body: { email, password, code: needCode ? code : undefined, deviceName: await deviceName(), deviceKind: deviceKind() } });
      await done(res);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'two_factor_required') {
        setNeedCode(true);
        setError(null);
        return;
      }
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Your connected mailboxes, settings and reminders sync to this device."
      footer={
        <>
          New to MailPilot? <Link to="/signup" className="font-semibold text-primary-ink hover:underline">Create an account</Link>
        </>
      }
    >
      {params.get('expired') && <Notice tone="warn" className="mb-4">Your session ended. Please sign in again.</Notice>}
      {params.get('revoked') && <Notice tone="warn" className="mb-4">This device was signed out from another device.</Notice>}
      <div className="mb-4">
        <SocialSignIn onSignedIn={done} />
      </div>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Email">{(id) => <Input id={id} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
        <Field label="Password">{(id) => <Input id={id} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        {needCode && (
          <Field label="Code from your authenticator app" hint="Lost your phone? Enter one of your recovery codes instead.">
            {(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" required autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />}
          </Field>
        )}
        <InlineError error={error} />
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!email || !password || (needCode && !code.trim())}>
          Sign in
        </Button>
        <p className="text-center text-[0.8125rem]">
          <Link to="/recover" className="font-medium text-primary-ink hover:underline">Forgot your password?</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

type SignupKind = SignupWorkspace['kind'];

const KINDS: { kind: SignupKind; title: string; text: string; icon: ReactNode }[] = [
  { kind: 'personal', title: 'Personal', text: 'Just me. My accounts stay private to me.', icon: <User className="h-5 w-5" /> },
  { kind: 'organization', title: 'Organization', text: 'My team or company. Share accounts and organise them into divisions.', icon: <Building2 className="h-5 w-5" /> },
  { kind: 'join', title: 'Join a team', text: 'I have an invite code from my organization.', icon: <KeyRound className="h-5 w-5" /> },
];

/** Live preview of an invite code ("You're joining Acme Inc."). */
function InvitePreview({ code }: { code: string }) {
  const c = useDebounced(code.trim(), 350);
  const enabled = c.replace(/[^A-Za-z0-9]/g, '').length >= 12;
  const q = useQuery({
    queryKey: ['invite', c],
    queryFn: () => api('GET /api/invites/:code', { params: { code: c } }),
    enabled,
    retry: false,
  });
  if (!enabled) return null;
  if (q.isFetching) return <p className="flex items-center gap-2 text-xs text-muted"><Spinner className="h-3.5 w-3.5" /> Checking code…</p>;
  if (q.error) return <p className="text-xs text-danger-ink">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-line bg-raised p-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl font-semibold text-white" style={{ background: q.data.workspaceColor }}>
        {q.data.workspaceName.charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 text-[0.8125rem]">
        <p className="truncate font-semibold text-ink">You're joining {q.data.workspaceName}</p>
        <p className="truncate text-xs text-muted">
          As {q.data.role === 'admin' ? 'an admin' : 'a member'}{q.data.invitedBy ? ` · invited by ${q.data.invitedBy}` : ''}
          {q.data.emailLocked ? ' · for a specific email address' : ''}
        </p>
      </div>
      <Check className="ml-auto h-5 w-5 shrink-0 text-success" aria-hidden />
    </div>
  );
}

export function SignupPage() {
  const [params] = useSearchParams();
  const inviteParam = params.get('invite') ?? '';
  const [kind, setKind] = useState<SignupKind | null>(inviteParam ? 'join' : null);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [divisions, setDivisions] = useState<string[]>([]);
  const [code, setCode] = useState(inviteParam);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const done = useAuthSuccess();
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const ready =
    !!email && password.length >= MIN_PASSWORD && !!displayName.trim() && (kind !== 'organization' || !!orgName.trim()) && (kind !== 'join' || code.trim().length >= 4);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!kind || !ready) return;
    setBusy(true);
    setError(null);
    const workspace: SignupWorkspace =
      kind === 'organization' ? { kind, name: orgName.trim(), divisions } : kind === 'join' ? { kind, code: code.trim() } : { kind: 'personal' };
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await api('POST /api/auth/signup', { body: { email, password, displayName, deviceName: await deviceName(), deviceKind: deviceKind(), timezone, workspace } });
      await done(res);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={kind === null ? 'Create your MailPilot account' : kind === 'organization' ? 'Set up your organization' : kind === 'join' ? 'Join your team' : 'Create your personal workspace'}
      subtitle={kind === null ? 'How will you use MailPilot? You can create or join organizations later too.' : 'Your app account is separate from the email accounts you connect later.'}
      footer={
        <>
          Already have an account? <Link to="/login" className="font-semibold text-primary-ink hover:underline">Sign in</Link>
        </>
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {kind === null ? (
          <motion.div key="choose" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={spring} className="space-y-2.5" aria-label="Workspace type">
            {KINDS.map((k) => (
              <button
                key={k.kind}
                type="button"
                onClick={() => setKind(k.kind)}
                className="hover-lift group flex w-full items-center gap-4 rounded-2xl border border-line bg-surface p-4 text-left hover:border-primary/50 hover:bg-primary-soft/40"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary-ink transition-colors group-hover:bg-primary group-hover:text-primary-fg">{k.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[0.9375rem] font-semibold text-ink">{k.title}</span>
                  <span className="block text-[0.8125rem] text-muted">{k.text}</span>
                </span>
                <ArrowLeft className="h-4 w-4 rotate-180 text-muted transition-transform group-hover:translate-x-1 group-hover:text-primary-ink" aria-hidden />
              </button>
            ))}
          </motion.div>
        ) : (
          <motion.form key="details" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={spring} onSubmit={submit} className="space-y-4" noValidate>
            <button type="button" onClick={() => setKind(null)} className="-mt-2 inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[0.8125rem] font-medium text-muted hover:bg-raised hover:text-ink">
              <ArrowLeft className="h-4 w-4" />
              {KINDS.find((k) => k.kind === kind)?.title} · change
            </button>
            {kind === 'organization' && (
              <>
                <Field label="Organization name">{(id) => <Input id={id} autoComplete="organization" required value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Acme Inc." />}</Field>
                <Field label="Divisions (optional)" hint="Teams like Sales or Support. Accounts can be limited to a division later.">
                  {(id) => <ChipInput id={id} label="Divisions" value={divisions} onChange={setDivisions} placeholder="Type a name and press Enter" />}
                </Field>
              </>
            )}
            {kind === 'join' && (
              <>
                <Field label="Invite code" hint="Ask an admin of your organization for one.">
                  {(id) => <Input id={id} required value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-EFGH-JKLM" autoCapitalize="characters" className="font-mono tracking-wider" />}
                </Field>
                <InvitePreview code={code} />
              </>
            )}
            <SocialSignIn
              verb="Sign up"
              onSignedIn={done}
              workspace={kind === 'organization' ? { kind, name: orgName.trim(), divisions } : kind === 'join' ? { kind, code: code.trim() } : { kind: 'personal' }}
              blocked={kind === 'organization' && !orgName.trim() ? 'Enter your organization name above first.' : kind === 'join' && code.trim().length < 4 ? 'Enter your invite code above first.' : null}
            />
            <Field label="Your name">{(id) => <Input id={id} autoComplete="name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}</Field>
            <Field label="Email">{(id) => <Input id={id} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
            <Field label="Password" hint={`At least ${MIN_PASSWORD} characters.`} error={tooShort ? `Use at least ${MIN_PASSWORD} characters.` : undefined}>
              {(id) => <Input id={id} type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}
            </Field>
            <InlineError error={error} />
            <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!ready} className="hover-glow">
              {kind === 'organization' ? 'Create organization' : kind === 'join' ? 'Join and create account' : 'Create account'}
            </Button>
            <p className="text-center text-xs text-muted">
              By creating an account you agree to the <Link to="/terms" className="text-primary-ink hover:underline">Terms</Link> and <Link to="/privacy" className="text-primary-ink hover:underline">Privacy policy</Link>.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}

/** /join/:code — signed in: confirm joining; signed out: sign up with the code filled in. */
export function JoinPage() {
  const { code = '' } = useParams();
  const me = useMe();
  const join = useJoinOrganization();
  const navigate = useNavigate();
  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading" />
      </div>
    );
  }
  if (!me.data) return <Navigate to={`/signup?invite=${encodeURIComponent(code)}`} replace />;
  return (
    <AuthLayout title="Join an organization" subtitle={`Signed in as ${me.data.user.email}.`} footer={<Link to="/inbox" className="font-semibold text-primary-ink hover:underline">Not now</Link>}>
      <div className="space-y-4">
        <InvitePreview code={code} />
        <InlineError error={join.error} />
        <Button variant="primary" size="lg" block loading={join.isPending} onClick={() => join.mutate(code)}>
          Join organization
        </Button>
        <Button size="lg" block onClick={() => navigate('/inbox')}>
          Cancel
        </Button>
      </div>
    </AuthLayout>
  );
}

export function RecoverPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ devResetUrl?: string } | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api('POST /api/auth/recover', { body: { email } });
      setSent({ devResetUrl: res.devResetUrl });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  // devResetUrl may be absolute (server origin) — keep only path+query so it works through the dev proxy too
  const devLink = sent?.devResetUrl
    ? (() => {
        try {
          const u = new URL(sent.devResetUrl, window.location.origin);
          return `${u.pathname}${u.search}`;
        } catch {
          return sent.devResetUrl;
        }
      })()
    : null;
  return (
    <AuthLayout title="Recover your account" subtitle="We'll send a reset link if an account exists for this email." footer={<Link to="/login" className="font-semibold text-primary-ink hover:underline">Back to sign in</Link>}>
      {sent ? (
        <div className="space-y-4">
          <Notice tone="success">If an account exists for {email}, a reset link is on its way.</Notice>
          {devLink && (
            <Notice tone="warn">
              <p className="font-semibold">Development mode</p>
              <p className="mt-1">No system mailer is configured, so the reset link is shown here instead of being emailed:</p>
              <Link to={devLink.replace(/^.*?\/reset/, '/reset')} className="mt-2 inline-block break-all font-semibold underline">
                {devLink}
              </Link>
            </Notice>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email">{(id) => <Input id={id} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
          <InlineError error={error} />
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!email}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatch || password.length < MIN_PASSWORD) return;
    setBusy(true);
    setError(null);
    try {
      await api('POST /api/auth/reset', { body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Choose a new password" footer={<Link to="/login" className="font-semibold text-primary-ink hover:underline">Back to sign in</Link>}>
      {!token ? (
        <Notice tone="danger">This reset link is missing its token. Request a new one from the recovery page.</Notice>
      ) : done ? (
        <div className="space-y-4">
          <Notice tone="success">Password updated. Other devices stay signed in until you revoke them in Security settings.</Notice>
          <Link to="/login" className="block">
            <Button variant="primary" size="lg" block>Sign in</Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="New password" hint={`At least ${MIN_PASSWORD} characters.`}>{(id) => <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
          <Field label="Confirm password" error={mismatch ? "Passwords don't match." : undefined}>
            {(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
          </Field>
          <InlineError error={error} />
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={password.length < MIN_PASSWORD || mismatch || !confirm}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
